import { preloadImage } from '@mail/3promo';
import { Icon20Cancel, Icon20ChevronUp } from '@vkontakte/icons';
import { Button, Spacing, Text } from '@vkontakte/vkui';
import leftStarBig from 'img/aiTariff/leftStarBig.png';
import leftStarSmall from 'img/aiTariff/leftStarSmall.png';
import rightObject from 'img/aiTariff/rightObject.png';
import starRight from 'img/aiTariff/starRight.png';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiTariffAbVariantB, newNeuroThreeTariffsPopup } from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { trial256gbNeurofilterPromo } from 'reactApp/appHelpers/featuresHelpers/features/neurofilter';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import type { IProps } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { renderCommonWhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog.helpers';
import { IMAGE_PRELOAD_TIMEOUT } from 'reactApp/constants/magicIdentificators';
import { sendDwhModal, sendDwhRadar } from 'reactApp/modules/dwh/dwh.helpers';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openThreeTariffsPopup } from 'reactApp/modules/payment/payment.helpers';
import { openFastCheckout } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { UserStorageActions, UserStorageSelectors } from 'reactApp/modules/user/userStorage';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { noopVoid } from 'reactApp/utils/helpers';

import styles from './NeuroPromoObject.css';

const name = 'mnz_buy_with_neiro';
const modalName = 'mnz_neiro_tariff_from_cloud_object';
const paySourceName = 'try_neiro_tariff_from_cloud_modal';
const PROMO_ID = 'neuro-promo-object-shown-2';
const PROMO_ID_SESSION_CLOSE = `${PROMO_ID}sess-close`;

export const videoUrlLight = `${cdnPackagesUrl}/static/cloud/ai/ai-tariff/cloud_modal_light.mp4`;
export const videoUrlDark = `${cdnPackagesUrl}/static/cloud/ai/ai-tariff/cloud_modal_dark.mp4`;

function setSessionCloseStatus() {
    try {
        sessionStorage.setItem(PROMO_ID_SESSION_CLOSE, 'true');
    } catch {}
}

function getSessionCloseStatus() {
    try {
        return sessionStorage.getItem(PROMO_ID_SESSION_CLOSE);
    } catch {}
}

const renderPopup = (videoUrl, payClick = noopVoid) => {
    const data: IProps = {
        title: (
            <span>
                {aiTariffAbVariantB
                    ? 'Новый тариф с нейросетью в Облаке — Mail Space Нейро'
                    : 'На фото вы и море — улучшить тут можно только качество. С нейросетью в подписке Mail Space'}
            </span>
        ),
        content: (
            <>
                <Spacing size={14} />
                <>
                    Уберёт шум, добавит контрастности и поможет передать момент.
                    <br />А ещё создаст изображения по вашему запросу в 8 разных стилях
                </>
                <Spacing size={24} />
            </>
        ),
        primaryButtonText: <span>Попробовать за&nbsp;1&nbsp;₽</span>,
        imageUrl: '',
        imageOnlyAdditionalContent: true,
        imageAdditionalContent: <video src={videoUrl} autoPlay muted loop className={styles.image} />,
        onClick: () => {
            sendDwhModal({ name: modalName, action: 'click' });
            payClick();
        },
        onDismiss: () => {
            sendDwhModal({ name: modalName, action: 'close' });
        },
        onShow: () => {
            sendDwhModal({ name: modalName });
            productsController.updateProducts(trial256gbNeurofilterPromo);
        },
        dialogSize: 'large',
        buttonTheme: 'vk',
        closeAfterClick: true,
        contentClassName: styles.content,
        imageClassName: styles.image,
        imageWrapperClassName: styles.imageWrapper,
        closeIconClassName: styles.closeIcon,
        closeIconWrapperClassName: styles.closeIconWrapper,
        qaId: 'neuro-prm-pp',
    };

    return renderCommonWhatsNewDialog(data);
};

export const NeuroPromoObject: React.FC = () => {
    const dispatch = useDispatch();
    const [show, setShow] = useState(false);
    const [isCompleted, setIsCompleted] = React.useState<boolean>(false);
    const isOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const wasShownAndClosed = useSelector((state) => UserStorageSelectors.get(state, PROMO_ID)) || 0;
    const isViewerOpening = useSelector(ViewerSelectors.isViewerOpening);
    const doNotShow = isOverquota || wasShownAndClosed || isViewerOpening || !show || isCompleted || getSessionCloseStatus();
    const videoUrl = isDarkTheme ? videoUrlDark : videoUrlLight;

    useEffect(() => {
        setTimeout(() => setShow(true), 250);
    }, []);

    useEffect(() => {
        if (doNotShow) {
            return;
        }

        sendDwhRadar({ action: 'show', t: 'object', name });
    }, [doNotShow]);

    useEffect(() => {
        if (doNotShow) {
            return;
        }
    }, [doNotShow]);

    const payClick = useCallback(async () => {
        const paySource = getPaySource(paySourceName);

        if (newNeuroThreeTariffsPopup) {
            openThreeTariffsPopup({ dispatch, paySource });

            return;
        }

        dispatch(
            openFastCheckout({
                paySource,
                productId: trial256gbNeurofilterPromo,
                hideOtherTariffs: true,
                title: 'Подписка Mail Space с нейросетью на месяц бесплатно',
                featuresList: [
                    'Функции нейросети',
                    'Без рекламы в Почте и Облаке',
                    'Загрузка файлов до 100 ГБ в Облако',
                    'Отправка файлов до 100 ГБ в письмах',
                    'Добавление ещё 3 участников',
                    'Пароли для совместных папок в веб-версии',
                    'Приоритетная поддержка',
                ],
            })
        );
    }, []);

    const handleClick = useCallback(() => {
        sendDwhRadar({ action: 'click', t: 'object', name });

        dispatch(UserStorageActions.set(PROMO_ID, 1));

        preloadImage({ src: videoUrl, timeout: IMAGE_PRELOAD_TIMEOUT });

        renderPopup(videoUrl, payClick);
    }, [videoUrl, payClick]);

    const handleClose = useCallback((event) => {
        event.stopPropagation();

        sendDwhRadar({ action: 'close', t: 'object', name });

        setIsCompleted(true);
        setSessionCloseStatus();
    }, []);

    if (doNotShow) {
        return null;
    }
    return (
        <div className={styles.promo} data-test-id="neuro-promo-widget" onClick={handleClick}>
            <img src={leftStarSmall} className={styles.leftStarSmall} />
            <img src={leftStarBig} className={styles.leftStarBig} />
            <img src={starRight} className={styles.starRight} />
            <div className={styles.imageContainer}>
                <img src={rightObject} className={styles.rightObject} />
            </div>

            <div className={styles.promoContent}>
                <Text className={styles.title}>
                    Улучшить фото
                    <br />
                    за пару секунд
                </Text>
                <Spacing size={6} />
                <Text className={styles.description}>Нейросеть и не такое умеет. Попробуйте, это просто</Text>
                <Button className={styles.button} size={'s'} appearance={'neutral'} stretched={false}>
                    Узнать больше
                </Button>
            </div>

            <div className={styles.closeButton} onClick={handleClose}>
                <Icon20ChevronUp className={styles.iconChevron} />
                <Icon20Cancel className={styles.iconCancel} />
            </div>
        </div>
    );
};
